import React, { useEffect, useRef, useContext } from "react";
import ScrollTrigger from "gsap/ScrollTrigger";
import PageTop from "../components/PageTop";
import { CursorContext } from "../context/CursorContext";
import { transition1 } from "../transitions";
import { motion } from "framer-motion";
import Footer from "../components/Footer";

const DietPlan = () => {
  const scrollRef = useRef(null);
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);

  useEffect(() => {
    // 🧩 Set the default scroller for GSAP's ScrollTrigger to the custom scroll container
    ScrollTrigger.defaults({
      scroller: scrollRef.current,
    });

    // 🧩 Refresh all ScrollTriggers (in case layout changes)
    ScrollTrigger.refresh();
  }, []);

  // mealsData.js (or inside your component)
  const meals = [
    {
      id: 1,
      time: "4:30 AM",
      title: "Early Morning",
      emoji: "🌅",
      items: ["🍋 Lime + hot water", "✨ Detox & hydration"],
      macros: "0P / 0C / 0F",
    },
    {
      id: 2,
      time: "5:15 AM",
      title: "Pre-workout",
      emoji: "🏋️‍♂️",
      items: [
        "🍌 1 Banana",
        "🌴 Dates (3–4 pcs)",
        "🍇 Raisins (10g)",
        "⚡ Quick carbs for energy",
      ],
      macros: "1.5P / 51C / 0F",
    },
    {
      id: 3,
      time: "7:15 AM",
      title: "Post-workout",
      emoji: "💪",
      items: ["⚡ Creatine (3–5g)", "🚀 Recovery booster"],
      macros: "0P / 0C / 0F",
    },
    {
      id: 4,
      time: "8:30 AM",
      title: "Breakfast",
      emoji: "🍳",
      items: [
        "🍚 Rice (80g raw)",
        "🥚 3 Whole eggs + 2 whites (omelette w/ onion, tomato, capsicum)",
        "💊 Fish oil + Multivitamin",
        "🍴 Strong protein + carbs + micros",
      ],
      macros: "29P / 60C / 16F",
    },
    {
      id: 5,
      time: "11:00 AM",
      title: "Morning Snack",
      emoji: "🥤",
      items: [
        "🌱 Soya chunks (50g)",
        "🍌 Banana",
        "💪 Plant + fruit combo for steady energy",
      ],
      macros: "27P / 32C / 2F",
    },
    {
      id: 6,
      time: "1:30 PM",
      title: "Lunch",
      emoji: "🍗",
      items: [
        "🍗 Chicken breast (120g)",
        "🍚 Rice (80g raw)",
        "🥦 Veggies (broccoli/cauliflower + carrot)",
        "🍴 Clean protein + carb base",
      ],
      macros: "38P / 60C / 3F",
    },
    {
      id: 7,
      time: "4:30 PM",
      title: "Evening Snack",
      emoji: "🥒",
      items: [
        "🍗 Chicken breast (120g)",
        "🍚 Rice (80g raw) + 🥛 Curd (250ml milk)",
        "🥒 Cucumber",
        "⚡ Keeps protein flow steady",
      ],
      macros: "38P / 65C / 7F",
    },
    {
      id: 8,
      time: "7:00 PM",
      title: "Dinner",
      emoji: "🍠",
      items: [
        "🍗 Chicken breast (120g)",
        "🥔 Potato (100g boiled)",
        "🍉 Fruit (watermelon/pineapple ~150g)",
        "✨ Balanced night meal, light on digestion",
      ],
      macros: "38P / 44C / 2F",
    },
    {
      id: 9,
      time: "8:30 PM",
      title: "Before Bed",
      emoji: "🌙",
      items: [
        "🍯 Honey (1 tsp)",
        "🍏 Amla",
        "😴 Optional: warm milk (if needed for sleep)",
        "🌙 Sweet end, supports recovery",
      ],
      macros: "0P / 5C / 0F",
    },
  ];

  return (
    <div
      ref={scrollRef}
      className="h-screen overflow-y-scroll snap-y snap-mandatory snap-start scroll-smooth"
    >
      <motion.section
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        viewport={{ once: false, amount: 0.2 }}
        transition={transition1}
        className="h-screen snap-start pt-24 lg:pt-36 pb-8 flex flex-col lg:flex-row justify-center items-center"
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >
        <div className="px-4">
          <div className="mb-4 font-bold text-lg">
            🥗 Daily Plan (2000–2200 kcal)
          </div>

          {/* Meals container scrollable */}
          <div
            className="flex-1 overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 200px)" }} // 200px = heading + summary heights
          >
            <div className="flex flex-col gap-4 justify-center items-center pb-8 lg:pb-16">
              {meals.map((meal) => (
                <div
                  key={meal.id}
                  className="p-4 rounded-xl shadow-md border bg-teal-400/10 dark:bg-gray-800  border-gray-300/20 w-full flex-1"
                >
                  <div className="mb-1 font-semibold">
                    {meal.emoji} Meal {meal.id} – {meal.title} ({meal.time})
                  </div>
                  {meal.items.map((item, index) => (
                    <div key={index} className="mb-1">
                      {item}
                    </div>
                  ))}
                  <div className="mb-1 font-semibold">
                    Macros: {meal.macros}
                  </div>
                </div>
              ))}
              <div className="mb-4  p-4 rounded-xl shadow-md border w-full border-gray-300/20 bg-teal-400/10 dark:bg-gray-800 ">
                <div className="mb-1 font-semibold">📊 Daily Totals</div>
                <div className="mb-1">🍗 Protein: ~160g</div>
                <div className="mb-1">🍚 Carbs: ~235g</div>
                <div className="mb-1">🥑 Fat: ~50g</div>
                <div className="mb-1 font-semibold">
                  🔥 Calories: ~2,000–2,200
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <PageTop scrollContainerRef={scrollRef} />
      <Footer />
    </div>
  );
};

export default DietPlan;
